import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { fetchUserInfo, signOutFirebase } from '../services/userService';
import { isDemoMode } from '../utils/demoMode';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true); // Add loading state
  const [loggingIn, setLoggingIn] = useState(false); // Add logging in state
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isExpired = searchParams.get('expired') === 'true';

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Don't automatically redirect - validate user first
        try {
          const userInfo = await fetchUserInfo();
          if (userInfo && (userInfo.userType === 'admin' || userInfo.userType === 'demo')) {
            if (userInfo.userType === 'demo' && !user.emailVerified) {
              // Demo user with unverified email — send to verify-email page
              navigate('/verify-email');
            } else {
              navigate('/');
            }
          } else {
            await signOutFirebase();
            setLoggingIn(false);
            setError('Login not allowed: only admin users can log in.');
          }
        } catch (err) {
          await signOutFirebase();
          setLoggingIn(false);
          setError('Login not allowed: unable to retrieve user info.');
        }
      }
      setLoading(false); // Finish loading check
    });
    return () => unsubscribe(); // Cleanup subscription
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoggingIn(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // The onAuthStateChanged listener will handle validation and redirect
    } catch (err) {
      setError('Failed to log in. Please check your email and password.');
      console.error("Login error:", err);
      setLoggingIn(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4 py-12 sm:px-6 lg:px-8 font-sans">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl ring-1 ring-gray-900/5 p-8 sm:p-10">
        
        <div className="sm:mx-auto sm:w-full sm:max-w-md flex flex-col items-center mb-8">
          <img
            className="mx-auto h-12 w-auto mb-4"
            src="/logo-light.png"
            alt="Loope Logo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h2 className="text-center text-3xl font-extrabold tracking-tight text-gray-900">
            Sign in to Loope
          </h2>
          <span className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20 mt-3">
            Administrator Portal
          </span>
        </div>

        {isExpired && (
          <div className="rounded-xl bg-orange-50 border border-orange-200 p-4 mb-4 text-sm text-orange-800">
            Your demo access has expired. Please <Link to="/demo-signup" className="underline font-medium">request a new demo</Link> if you'd like to continue.
          </div>
        )}

        <form className="space-y-6" onSubmit={handleLogin}>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email address
            </label>
            <div className="mt-1">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="block w-full rounded-xl border-0 px-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <Link to="/forgot-password" className="text-sm font-medium text-green-600 hover:text-green-500">
                Forgot password?
              </Link>
            </div>
            <div className="relative mt-1">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                className="block w-full rounded-xl border-0 px-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-600 hover:text-green-600"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-xl bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loggingIn}
              className="flex w-full justify-center rounded-full bg-green-600 px-8 py-3.5 text-sm font-bold text-white shadow-md hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loggingIn ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </div>
        </form>

        {isDemoMode() && (
          <p className="mt-6 text-center text-sm text-gray-500">
            Want to explore the demo?{' '}
            <Link to="/demo-signup" className="font-medium text-orange-600 hover:text-orange-500">
              Request demo access
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
